import React, {
  Fragment,
  isValidElement,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { Link } from 'gatsby'
import { get, keyBy, merge } from 'lodash'
import { Box, FormControlLabel, Switch, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useSearchFilters } from '../../../providers/search'
import { formatDate, formatLinkWithTicker } from '../../../utils/functions'
import AsOfDate from '../../AsOfDate'
import Html from '../../Html/Html'
import BenchmarkScrollingTable from '../../Tables/BenchmarkScrollingTable'
import { percentageRenderer } from '../../Tables/ScrollingTable'
import ExpenseRatio from '../ExpenseRatio'

const NoDecorationLink = styled(Link)(() => ({
  textDecoration: 'none',
}))

const WeightedTypography = styled(Typography)(() => ({
  fontWeight: 500,
}))

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(2),
}))

const HtmlLink = styled('div')(() => ({
  '& a': {
    textDecoration: 'none',
    color: '#D8B45A',
  },
}))

const TextLink = styled('button')(() => ({
  background: 'none',
  color: '#D8B45A',
  textDecoration: 'underline',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
}))

const NetExpenseRatioCell = memo(function NetExpenseRatioCell({ value, row }) {
  return (
    <ExpenseRatio ratio={value} popup={get(row, 'expense_ratio.net_popup')} />
  )
})

const GrossExpenseRatioCell = memo(function GrossExpenseRatioCell({
  value,
  row,
}) {
  return (
    <ExpenseRatio ratio={value} popup={get(row, 'expense_ratio.gross_popup')} />
  )
})

const AdjustedExpenseRatioCell = memo(function AdjustedExpenseRatioCell({
  value,
  row,
}) {
  return (
    <ExpenseRatio
      ratio={value}
      popup={get(row, 'expense_ratio.adjusted_popup')}
    />
  )
})

export default function AllFundPerformance({
  data = [],
  fundGrouping,
  activeShareClass,
  presentation,
  html_content_before = [],
  isMutualFund,
}) {
  const [showMarketValues, setShowMarketValues] = useState(false)
  const { filters, applyFilter, currentMsFilter } = useSearchFilters()

  const queryData = useMemo(() => data?.filter((fund) => fund !== null), [data])

  const asOfDate = useMemo(
    () =>
      queryData.find(
        (fundClass) => get(fundClass, 'performance.calendar.date') !== undefined
      ),
    [queryData]
  )

  const dateRenderer = useCallback(({ value }) => {
    return <span>{value ? formatDate(value) : ''}</span>
  }, [])

  /** Use a callback that references a styled Link + styled Typography */
  const fundNameRenderer = useCallback(({ row, value }) => {
    if (row && row.benchmarkIndex) {
      return <span className="benchmark-name">{value}</span>
    }
    return (
      <span>
        <NoDecorationLink to={formatLinkWithTicker(row)}>
          <WeightedTypography variant="body2" color="text.primary">
            {value}
          </WeightedTypography>
        </NoDecorationLink>
      </span>
    )
  }, [])

  const accesors = useMemo(() => {
    const baseAccesors = {
      ytdReturn: 'yearToDate',
      oneYear: 'oneYear',
      threeYear: 'threeYear',
      fiveYear: 'fiveYear',
      tenYear: 'tenYear',
      sinceInception: 'sinceInception',
    }
    if (!showMarketValues) {
      return baseAccesors
    }
    return {
      ytdReturn: (d) => d?.marketReturn?.yearToDate,
      oneYear: (d) => d?.marketReturn?.oneYear,
      threeYear: (d) => d?.marketReturn?.threeYear,
      fiveYear: (d) => d?.marketReturn?.fiveYear,
      tenYear: (d) => d?.marketReturn?.tenYear,
      sinceInception: (d) =>
        d?.marketReturn?.sinceInception ?? d?.sinceInception,
    }
  }, [showMarketValues])

  const genericColumns = useMemo(() => {
    return [
      {
        Header: 'Fund Name',
        accessor: 'title',
        id: 'title',
        Cell: fundNameRenderer,
        sortable: true,
      },
      {
        Header: 'Ticker',
        accessor: 'ticker',
        id: 'ticker',
        Cell: fundNameRenderer,
        sortable: true,
      },
      {
        Header: 'Inception Date',
        accessor: 'inception_date',
        id: 'inception_date',
        Cell: dateRenderer,
        sortable: true,
      },
      {
        Header: 'YTD Return',
        accessor: accesors.ytdReturn,
        id: 'ytdReturn',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: '1 Yr.',
        accessor: accesors.oneYear,
        id: 'oneYear',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: '3 Yr.',
        accessor: accesors.threeYear,
        id: 'threeYear',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: '5 Yr.',
        accessor: accesors.fiveYear,
        id: 'fiveYear',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: '10 Yr.',
        accessor: accesors.tenYear,
        id: 'tenYear',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: 'Since Inception',
        accessor: accesors.sinceInception,
        id: 'sinceInception',
        Cell: percentageRenderer,
        sortable: true,
      },
      {
        Header: 'Net Expense Ratio',
        accessor: (d) => d?.expense_ratio?.net,
        id: 'expense_ratio_net',
        Cell: NetExpenseRatioCell,
        sortable: true,
      },
      {
        Header: 'Gross Expense Ratio',
        accessor: (d) => d?.expense_ratio?.gross,
        id: 'expense_ratio_gross',
        Cell: GrossExpenseRatioCell,
        sortable: true,
      },
    ]
  }, [
    accesors.ytdReturn,
    accesors.oneYear,
    accesors.threeYear,
    accesors.fiveYear,
    accesors.tenYear,
    accesors.sinceInception,
    fundNameRenderer,
    dateRenderer,
  ])

  const adjColumns = useMemo(() => {
    return [
      {
        Header: 'Adjusted Expense Ratio',
        accessor: (d) => d?.expense_ratio?.adjusted,
        id: 'expense_ratio_adjusted',
        Cell: AdjustedExpenseRatioCell,
        sortable: true,
      },
    ]
  }, [])

  const findFundByShareClass = useCallback(
    (fund) => {
      const activeShareClassId = activeShareClass?.id
      return fund?.fund_classes?.find(
        (shareClass) => shareClass.share_class[0].id === activeShareClassId
      )
    },
    [activeShareClass]
  )

  const fundData = useMemo(() => keyBy(queryData, 'ticker'), [queryData])

  const hasMorningStarRating = useCallback((row) => {
    return row?.morningstar_rating !== undefined
  }, [])

  const tables = useMemo(() => {
    return fundGrouping.group.map((group) => {
      const columns = [...genericColumns]
      let showAdjusted = false
      let shaded = false
      const tableData = []
      const benchmarkData = []

      tableData.push({ title: group.title, innerTitle: group.title })
      benchmarkData.push({ title: group.title, innerTitle: group.title })

      group.products.forEach((fund) => {
        const fundByShareClass = findFundByShareClass(fund)
        if (!fundByShareClass) return

        const apiFund = fundData[fundByShareClass.ticker] || {}
        const fundReturn = get(apiFund, 'performance.fundReturn')
        const indexReturns = get(apiFund, 'performance.indexReturns', [])
        const marketReturn = get(apiFund, 'performance.marketReturn', [])

        const mergedFund = merge({}, fundReturn, fund, fundByShareClass)
        if (mergedFund.expense_ratio?.adjusted) {
          showAdjusted = true
        }

        const benchmarkobjs =
          indexReturns &&
          indexReturns.map((indexReturn) => {
            const bmobj = merge(indexReturn, {
              title: indexReturn.benchmarkIndex.name,
            })
            return bmobj
          })

        tableData.push({
          ...mergedFund,
          marketReturn,
          rowOptions: { shaded },
        })

        benchmarkData.push({
          ...mergedFund,
          marketReturn,
          rowOptions: { shaded },
        })

        if (benchmarkobjs) {
          benchmarkobjs.forEach((benchmarkObj) => {
            benchmarkData.push({
              ...benchmarkObj,
              rowOptions: { shaded },
            })
          })
        }
        shaded = !shaded
      })

      if (showAdjusted) {
        columns.push(...adjColumns)
      }

      return {
        columns,
        title: group.title,
        data: tableData,
        benchmarkData: benchmarkData,
      }
    })
  }, [
    fundGrouping.group,
    genericColumns,
    adjColumns,
    fundData,
    findFundByShareClass,
  ])

  const filteredTables = useMemo(() => {
    const output = []
    tables.forEach((table) => {
      const response = applyFilter(table, filters)
      if (response) {
        const tempBenchmark = response.benchmarkData.slice()
        if (currentMsFilter !== null) {
          for (let idx = 0; idx < tempBenchmark.length; idx++) {
            const row = tempBenchmark[idx]
            if (hasMorningStarRating(row)) {
              const rating =
                row.morningstar_rating && row.morningstar_rating.length > 0
                  ? row.morningstar_rating[0].rating
                  : 'NEUTRAL'
              if (rating !== currentMsFilter) {
                deleteBelow(tempBenchmark, idx)
              }
            }
          }
        }
        response.benchmarkData = tempBenchmark
        output.push(response)
      }
    })
    return output
  }, [tables, applyFilter, filters, currentMsFilter, hasMorningStarRating])

  function deleteBelow(list, index) {
    removeElement(list, index)
    while (list.length > 0 && index < list.length) {
      if (hasMorningStarRating(list[index])) break
      removeElement(list, index)
    }
  }

  function removeElement(list, index) {
    if (index > -1) {
      list.splice(index, 1)
    }
  }

  const combinedTable = useMemo(() => {
    const cTable = { data: [], benchmarkData: [] }
    filteredTables.forEach((table) => {
      cTable.data = cTable.data.concat(table.data)
      cTable.benchmarkData = cTable.benchmarkData.concat(table.benchmarkData)
    })
    return cTable
  }, [filteredTables])

  const hasMarketPrice = useMemo(() => {
    return data?.some((tickerData) =>
      Object.values(tickerData?.performance?.marketReturn ?? {}).some(
        (mr) => mr
      )
    )
  }, [data])

  const handleToggleShowMarketValues = useCallback(() => {
    setShowMarketValues((prev) => !prev)
  }, [])

  const handleClearFilters = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [])

  const stablePresentation = useMemo(() => {
    if (typeof presentation === 'object' && !isValidElement(presentation)) {
      return { ...presentation }
    }
    return null
  }, [presentation])

  const benchmarkScrollingProps = useMemo(() => {
    if (tables.length === 0) {
      return null
    }
    return {
      columns: tables[0].columns,
      data: filteredTables,
      benchmarkData: combinedTable.benchmarkData,
      presentation: stablePresentation,
      className: 'table-headers--centered',
    }
  }, [tables, filteredTables, stablePresentation])
  const switchControl = useCallback(<Switch />)

  return (
    <Fragment>
      <ControlsContainer>
        {asOfDate?.performance && (
          <AsOfDate date={asOfDate.performance.calendar.date} />
        )}
        {hasMarketPrice && !isMutualFund && (
          <FormControlLabel
            control={switchControl}
            label="NAV/Market Price"
            labelPlacement="start"
            onChange={handleToggleShowMarketValues}
          />
        )}
      </ControlsContainer>

      {html_content_before.map((content_block, idx) => (
        <HtmlLink key={`html-block-${idx}`}>
          <Html content={content_block.html} />
        </HtmlLink>
      ))}

      {combinedTable.data.length === 0 ? (
        <Typography variant="body2">
          Your criteria produced no results.{' '}
          <TextLink onClick={handleClearFilters}>Clear filter</TextLink> to
          explore our products.
        </Typography>
      ) : (
        benchmarkScrollingProps && (
          <BenchmarkScrollingTable {...benchmarkScrollingProps} />
        )
      )}
    </Fragment>
  )
}